import buildSubjectListPath from '@UI/Pages/Beneficiaries/Shared/Helpers/buildSubjectListPath';
import RemoveSubjectMember from '@Application/Subjects/Members/Commands/RemoveSubjectMember';
import PutSubjectMember, {
	type SubjectMemberAccess,
} from '@Application/Subjects/Members/Commands/PutSubjectMember';
import putSubjectMember from '@Application/Subjects/Members/putSubjectMember';
import removeSubjectMember from '@Application/Subjects/Members/removeSubjectMember';
import type SubjectAddress from '@Domain/Subjects/SubjectAddress';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { theme } from '@Guidelines/Theme/theme';
import ignoreHandledError from '@UI/Helpers/ignoreHandledError';
import PersonRow from '@UI/Pages/Beneficiaries/Shared/SubjectScreen/PersonRow';
import useStrictAuthenticatedUser from '@UI/Providers/AuthenticatedUserProvider/useStrictAuthenticatedUser';
import type SubjectMemberView from '@UI/Store/Subjects/Views/SubjectMemberView';
import type SubjectUserView from '@UI/Store/Subjects/Views/SubjectUserView';
import { css } from '@emotion/react';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from '@verclam/synapse';
import { Button, confirmDialog, Dropdown } from '@verclam/ui-kit';

const UNKNOWN_PERSON_LABEL = 'Personne inconnue';
const UNKNOWN_PERSON_INITIALS = '?';

const ACCESS_OPTIONS: ReadonlyArray<{ readonly value: SubjectMemberAccess; readonly label: string }> = [
	{ value: 'full', label: 'Accès complet' },
	{ value: 'read_only', label: 'Lecture seule' },
];

const selfSuffixStyle = css`
	font-weight: ${theme.fontWeights.normal};
	color: ${theme.colors.inkSecondary};
`;

const readOnlyAccessStyle = css`
	font-family: ${theme.fonts.body};
	font-size: ${theme.fontSizes.small};
	font-weight: ${theme.fontWeights.normal};
	color: ${theme.colors.inkMuted};
`;

const accessStyle = css`
	width: 11rem;
`;

type Props = {
	readonly member: SubjectMemberView;
	/** The name of the member; `null` when the users of the dossier do not list them. */
	readonly user: SubjectUserView | null;
	readonly subject: SubjectAddress;
	readonly facilityId: number;
	/** Without the right to share, the row only reads: no access choice, no removal. */
	readonly canShare: boolean;
	/** Called once the signed-in member has lowered their own access. */
	readonly onOwnAccessLowered: () => void;
};

/**
 * Lowering one's own access and removing a member each ask for a confirmation; raising an access asks
 * for none. A member who removes themselves leaves for the list of the type, since the dossier may no
 * longer be visible to them: the navigation only runs once the server has accepted.
 */
const MemberRow: React.FC<Props> = ({ member, user, subject, facilityId, canShare, onOwnAccessLowered }) => {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const { user: currentUser } = useStrictAuthenticatedUser();
	const isSelf = currentUser.id === member.userId;
	const name = user?.fullName ?? UNKNOWN_PERSON_LABEL;

	const saveAccess = React.useCallback(
		(access: SubjectMemberAccess) => {
			dispatch(putSubjectMember(new PutSubjectMember(facilityId, subject, member.userId, access)))
				.then(() => {
					if (isSelf && access === 'read_only') {
						onOwnAccessLowered();
					}
				})
				.catch(ignoreHandledError);
		},
		[dispatch, facilityId, subject, member.userId, isSelf, onOwnAccessLowered],
	);

	const handleAccessChange = React.useCallback(
		(access: SubjectMemberAccess | null) => {
			if (access === null || access === member.access.value) {
				return;
			}

			if (isSelf && access === 'read_only') {
				confirmDialog({
					header: 'Passer en lecture seule',
					message: 'Vous ne pourrez plus modifier ce dossier ni gérer ses membres. Continuer ?',
					acceptLabel: 'Passer en lecture seule',
					accept: () => saveAccess(access),
				});
				return;
			}

			saveAccess(access);
		},
		[member.access.value, isSelf, saveAccess],
	);

	const handleRemoveClick = React.useCallback(() => {
		confirmDialog({
			header: 'Retirer ce membre',
			message: isSelf
				? 'Vous n’aurez plus accès à ce dossier après cette action. Continuer ?'
				: `Retirer ${name} des membres de ce dossier ?`,
			acceptLabel: 'Retirer',
			acceptSeverity: 'danger',
			accept: () => {
				dispatch(removeSubjectMember(new RemoveSubjectMember(facilityId, subject, member.userId)))
					.then(() => {
						if (isSelf) {
							navigate(buildSubjectListPath(subject.typeKey));
						}
					})
					.catch(ignoreHandledError);
			},
		});
	}, [dispatch, navigate, facilityId, subject, member.userId, isSelf, name]);

	return (
		<PersonRow
			initials={
				user === null
					? UNKNOWN_PERSON_INITIALS
					: `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase()
			}
			seed={member.userId}
			name={
				<React.Fragment>
					{name}
					<ConditionalLoader data={isSelf}>
						<span css={selfSuffixStyle}> (vous)</span>
					</ConditionalLoader>
				</React.Fragment>
			}
		>
			{canShare ? (
				<React.Fragment>
					<div css={accessStyle}>
						<Dropdown<SubjectMemberAccess>
							name={`memberAccess${member.userId}`}
							label={`Accès de ${name}`}
							isLabelHidden
							options={ACCESS_OPTIONS}
							optionLabel="label"
							optionValue="value"
							value={member.access.value as SubjectMemberAccess}
							onChange={handleAccessChange}
						/>
					</div>
					<Button
						type="button"
						text
						size="sm"
						severity="danger"
						label="Retirer"
						onClick={handleRemoveClick}
					/>
				</React.Fragment>
			) : (
				<span css={readOnlyAccessStyle}>{member.access.label}</span>
			)}
		</PersonRow>
	);
};

export default MemberRow;
